import { useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createApiCall } from 'services'

// VERA-790: internal "Tags" list page. Every encoded tag across the platform, associated
// either to a package (production) or directly to a company (company-direct test tags).
export type InternalTag = {
  id: string
  uid: string
  family?: 'ntag424_dna' | 'st25ta_e' | 'st25tv' | 'other'
  auth_profile?: string | null
  status?: 'active' | 'disabled' | 'retired'
  secret_name?: string | null
  /**
   * The EFFECTIVE owner — a tag's own company when it has one, otherwise its package's
   * (resolved server-side by the same COALESCE that joins the company, matching
   * `getOwnership`). NOT the raw `tags.company_id`, which is null for a package-derived tag.
   */
  company_id?: string | null
  company_name?: string | null
  company_logo?: string | null
  /** The tag's certificate, if one has been minted against it. Null otherwise. */
  certificate_id?: string | null
  encoded_at?: string | null
  package_id?: string | null
  package_name?: string | null
  product_sku?: string | null
  product_title?: string | null
  product_image?: string | null
  created_at: string
  updated_at: string
}

export type GetInternalTagsResponse = {
  data: InternalTag[]
  total: number
}

type UseInternalTagsParams = {
  limit?: number
  offset?: number
  companyId?: string
  family?: string
  status?: string
  search?: string
  sortBy?: string
  sortDir?: 'asc' | 'desc'
}

export const INTERNAL_TAGS_QUERY_KEY = 'internal-tags'

export const useInternalTags = (params: UseInternalTagsParams = {}) => {
  const stableParams = useMemo(() => {
    const base: Record<string, any> = {
      limit: params.limit ?? 25,
      offset: params.offset ?? 0,
    }
    if (params.companyId) base.companyId = params.companyId
    if (params.family) base.family = params.family
    if (params.status) base.status = params.status
    if (params.search) base.search = params.search
    // Sorting is server-side because the list is paginated — see listAll's SORTABLE
    // allow-list. Omitted entirely when unset so the API applies its default order.
    if (params.sortBy) {
      base.sortBy = params.sortBy
      base.sortDir = params.sortDir ?? 'desc'
    }
    return base
  }, [
    params.limit,
    params.offset,
    params.companyId,
    params.family,
    params.status,
    params.search,
    params.sortBy,
    params.sortDir,
  ])

  return useQuery<GetInternalTagsResponse>({
    queryKey: [INTERNAL_TAGS_QUERY_KEY, stableParams],
    queryFn: async () => {
      const response = await createApiCall({
        method: 'GET',
        url: '/packages/tags',
        params: stableParams,
      })
      return response.data as GetInternalTagsResponse
    },
    staleTime: 1000 * 60,
    placeholderData: (prev) => prev,
  })
}

// VERA-790 — Retire (soft, reversible) / Reactivate an encoded tag. Gated server-side to Vera
// Internal Admin via `internal_tag_retire`; the UI also disables the action for users without it.
export const useSetTagStatus = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, action }: { id: string; action: 'retire' | 'reactivate' }) => {
      const response = await createApiCall({
        method: 'PATCH',
        url: `/packages/tags/${id}/${action}`,
      })
      return response.data as { success: boolean; status: string }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [INTERNAL_TAGS_QUERY_KEY] })
    },
  })
}
